import { useQuery } from "@apollo/client";
import { uniqBy } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  AssessmentType,
  ComplianceProgramState,
  GetComplianceProgramsQuery,
  GetComplianceProgramsQueryVariables,
} from "types/graphql-schema";

import getComplianceProgramsQuery from "@apps/programs/graphql/get-compliance-programs-query";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import Button, { buttonTheme } from "@components/button/button";
import Callout from "@components/callout/callout";
import ComboboxGeneric, {
  ComboboxGenericOption,
} from "@components/combobox/generic-combobox";
import Loading from "@components/loading/loading";
import useDebounce from "@components/use-debounce/use-debounce";
import { assertEdgesNonNull, getUrlParam } from "@helpers/helpers";

import EngagementSurveyReport from "./engagement-survey-report";

const SurveyReportPage = () => {
  const label = useLabel();
  const location = useLocation();
  const history = useHistory();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 500);
  const programIdFromUrl = getUrlParam(location, "programId");
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(
    programIdFromUrl ? parseInt(programIdFromUrl) : null
  );
  const [availablePrograms, setAvailablePrograms] = useState<
    ComboboxGenericOption<number>[]
  >([]);

  const handleChangeProgam = useCallback(
    (programId: number, overrideUrl = false) => {
      const params = new URLSearchParams(location.search);
      params.set("programId", `${programId}`);
      const url = `${location.pathname}?${params}`;
      if (overrideUrl) {
        history.replace(url);
      } else {
        history.push(url);
      }
    },
    [history, location]
  );

  const organization = currentOrganizationVar();
  const { loading: isLoadingProgamList } = useQuery<
    GetComplianceProgramsQuery,
    GetComplianceProgramsQueryVariables
  >(getComplianceProgramsQuery, {
    variables: {
      search: debouncedQuery,
      organizationId: organization.id,
      state_In: [
        ComplianceProgramState.Published,
        ComplianceProgramState.Paused,
      ],
      assessmentType: AssessmentType.EngagementSurvey,
    },
    onCompleted: (response) => {
      const programs = response.detailedCompliancePrograms
        ? assertEdgesNonNull(response.detailedCompliancePrograms)
        : [];
      const programOptions = programs.map(({ id, title }) => ({
        value: id,
        label: title,
      }));
      setAvailablePrograms((prev) =>
        uniqBy([...programOptions, ...prev], "value")
      );
      if (!selectedProgramId) {
        if (programs.length > 0) {
          const programIds = programs.map((program) => program.id);
          const validProgramInUrl =
            selectedProgramId !== null &&
            programIds.includes(selectedProgramId);
          const programId = validProgramInUrl
            ? selectedProgramId
            : programs[0].id;
          handleChangeProgam(programId, true);
        } else {
          setSelectedProgramId(null);
        }
      }
    },
  });

  useEffect(() => {
    const programIdFromUrl = getUrlParam(location, "programId");
    if (programIdFromUrl) {
      const programId = parseInt(programIdFromUrl);
      setSelectedProgramId(programId);
    } else {
      setSelectedProgramId(null);
    }
    // only load program data when location changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  if (availablePrograms.length === 0 && isLoadingProgamList) {
    return (
      <div className="flex-1 flex justify-center p-10">
        <Loading>Loading...</Loading>
      </div>
    );
  }

  return (
    <div className="w-full">
      {availablePrograms.length === 0 ? (
        <div className="px-8">
          <Callout
            icon="info"
            title="No Programs Available"
            center
            className="py-6"
          >
            There is nothing to display on this page because your{" "}
            {label("organization")} has not created any Surveys.
            <Button
              text="Create Program"
              theme={buttonTheme.default}
              to="/programs/new"
            />
          </Callout>
        </div>
      ) : (
        selectedProgramId && (
          <div className="absolute inset-0 overflow-y-scroll pt-20">
            <EngagementSurveyReport
              isInReporting
              complianceProgramId={selectedProgramId}
              headerContent={
                <div className="flex-1 max-w-sm">
                  <ComboboxGeneric
                    prefix="Program"
                    options={availablePrograms}
                    onChangeValue={(opt) => handleChangeProgam(opt.value)}
                    value={
                      availablePrograms.find(
                        ({ value }) => value === selectedProgramId
                      ) ?? null
                    }
                    query={query}
                    setQuery={setQuery}
                  />
                </div>
              }
            />
          </div>
        )
      )}
    </div>
  );
};

export default SurveyReportPage;
